import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Col,
  Container,
  Row,
  Form,
  Button,
  Spinner,
  Table,
  Card,
  Tabs,
  Tab,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import styles from "./Account.style";
import TitleBar from "../../Components/TitleBar/TitleBar";
import TitleImage from "../../assets/images/about-us.png";
import Section from "../../Components/Section/Section";
import NoDataFound from "../../Components/NoDataFound/NoDataFound";
import FullScreenLoader from "../../Components/Modal/FullScreenLoader/FullScreenLoader";

// Custom hooks
import { useAccountStatement } from "./hooks/useAccountStatement";
import { useDateManager } from "./hooks/useDateManager";
import { useModalManager } from "./hooks/useModalManager";

// Components
import TransactionModal from "./components/TransactionModal";
import TableRenderer from "./components/TableRenderer";

// Constants
import {
  MODAL_NAMES,
  SEARCH_VALUES,
  TABLE_COLUMNS,
  TAB_KEYS,
} from "./constants";

// Utils
import { createTableRowData } from "./utils/tableData";
import { getStoreAccountDashboard } from "../../Api/apis/storeApi";

const Account: React.FC = React.memo(() => {
  const { t } = useTranslation();

  // Custom hooks
  const {
    loading,
    pdfLoading,
    transactionDataLoader,
    error,
    accountStatementData,
    transactionData,
    storeId,
    fetchAccountStatement,
    fetchTransactionData,
    downloadPDF,
  } = useAccountStatement();

  const {
    fromDate,
    toDate,
    tempFromDate,
    tempToDate,
    weeks,
    selectedWeek,
    setTempFromDate,
    setTempToDate,
    handleWeekChange,
    handleDateChange,
  } = useDateManager();

  const { modals, openModal, closeModal, isModalOpen } = useModalManager(
    Object.values(MODAL_NAMES)
  );

  const [activeKey, setActiveKey] = useState<string>(TAB_KEYS.WEEKLY);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [dashboardData, setDashboardData] = useState<any>(null);

  // Memoized values
  const breadcrumbs = useMemo(
    () => [t("footer.home"), t("footer.sellerEarning")],
    [t]
  );

  const title = useMemo(() => "Seller Earning", []);
  const imageUrl = useMemo(() => TitleImage, []);

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchAccountStatement(fromDate, toDate, true);
    }
  }, [fromDate, toDate, fetchAccountStatement]);

  // Event handlers
  const handleGoClick = useCallback(() => {
    handleDateChange();
    fetchAccountStatement(tempFromDate, tempToDate, false);
  }, [handleDateChange, fetchAccountStatement, tempFromDate, tempToDate]);

  const handleDownload = useCallback(() => {
    downloadPDF(fromDate, toDate);
  }, [downloadPDF, fromDate, toDate]);

  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index)
        ? prev.filter((row) => row !== index)
        : [...prev, index]
    );
  }, []);

  const handleTabSelect = useCallback((k: string | null) => {
    if (k !== null) {
      setActiveKey(k);
    }
  }, []);

  // Modal handlers
  const createModalHandler = useCallback(
    (modalName: string, searchValue: string) => {
      return async () => {
        try {
          openModal(modalName);
          await fetchTransactionData(fromDate, toDate, searchValue);
        } catch (error) {
          console.error(`Error opening ${modalName}:`, error);
        }
      };
    },
    [fetchTransactionData, fromDate, toDate, openModal]
  );

  const fetchDashboardData = async () => {
    try {
      const data = await getStoreAccountDashboard();
      setDashboardData(data.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  }

  useEffect(() => {
      fetchDashboardData();
  }, []);

  const modalHandlers = useMemo(
    () => ({
      handleShowPricePaidModal: createModalHandler(
        MODAL_NAMES.PRICE_PAID,
        SEARCH_VALUES.PRICE_PAID_BY_CUSTOMER
      ),
      handleShowDeliveryFeeModal: createModalHandler(
        MODAL_NAMES.DELIVERY_FEE,
        SEARCH_VALUES.DELIVERY_FEE_PAID_BY_CUSTOMER
      ),
      handleShowTotalPricePaidModal: createModalHandler(
        MODAL_NAMES.TOTAL_PRICE_PAID,
        SEARCH_VALUES.PRICE_PAID_BY_CUSTOMER
      ),
      handleShowPlatformCommissionModal: createModalHandler(
        MODAL_NAMES.PLATFORM_COMMISSION,
        SEARCH_VALUES.PLATFORM_COMMISSION
      ),
      handleShowTransactionModal: createModalHandler(
        MODAL_NAMES.TRANSACTION,
        SEARCH_VALUES.TRANSACTION_FEES
      ),
      handleShowOtherFeeOneModal: createModalHandler(
        MODAL_NAMES.OTHER_FEE_ONE,
        SEARCH_VALUES.HANDLING_CHARGES
      ),
      handleShowOtherFeeTwoModal: createModalHandler(
        MODAL_NAMES.OTHER_FEE_TWO,
        SEARCH_VALUES.OTHER_FEES
      ),
      handleShowPlatformDeliveryFeeModal: createModalHandler(
        MODAL_NAMES.PLATFORM_DELIVERY_FEE,
        SEARCH_VALUES.DELIVERY_FEES
      ),
      handleShowProductPriceUnderReturnWindowModal: createModalHandler(
        MODAL_NAMES.PRODUCT_PRICE_RETURN_WINDOW,
        SEARCH_VALUES.SALE_UNDER_RETURN_WINDOW
      ),
      handleShowDeliveryFeeUnderReturnWindowModal: createModalHandler(
        MODAL_NAMES.DELIVERY_FEE_RETURN_WINDOW,
        SEARCH_VALUES.SALE_UNDER_RETURN_WINDOW_DELIVERY
      ),
      handleShowProductPriceReturnRefundUnderProcessinModal: createModalHandler(
        MODAL_NAMES.PRODUCT_PRICE_RETURN_PROCESSING,
        SEARCH_VALUES.SALE_UNDER_PROCESSING
      ),
      handleShowDeliveryFeeReturnRefundUnderProcessinModal: createModalHandler(
        MODAL_NAMES.DELIVERY_FEE_RETURN_PROCESSING,
        SEARCH_VALUES.SALE_UNDER_PROCESSING_DELIVERY
      ),
      handleShowRefundedToCustomerModal: createModalHandler(
        MODAL_NAMES.REFUNDED_TO_CUSTOMER,
        SEARCH_VALUES.REFUND_PAID_TO_CUSTOMER
      ),
      handleShowCommissionRefundedOnReturnModal: createModalHandler(
        MODAL_NAMES.COMMISSION_REFUNDED,
        SEARCH_VALUES.REFUND_PLATFORM_COMMISSION
      ),
      handleShowTransactionFeeRefundedOnReturnModal: createModalHandler(
        MODAL_NAMES.TRANSACTION_FEE_REFUNDED,
        SEARCH_VALUES.REFUND_TRANSACTION_FEES
      ),
      handleShowOtherFeeOneRefundedOnReturnModal: createModalHandler(
        MODAL_NAMES.OTHER_FEE_ONE_REFUNDED,
        SEARCH_VALUES.REFUND_HANDLING_CHARGES
      ),
      handleShowOtherFeeTwoRefundedOnReturnModal: createModalHandler(
        MODAL_NAMES.OTHER_FEE_TWO_REFUNDED,
        SEARCH_VALUES.REFUND_OTHER_FEES
      ),
      // Add more handlers as needed
    }),
    [createModalHandler]
  );

  // Memoized modal configurations
  const modalConfigurations = useMemo(
    () => [
      {
        name: MODAL_NAMES.PRICE_PAID,
        title: "Product Price Paid By Customers",
        description: "Here are the details of the price paid by the customer:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Product Price",
      },
      {
        name: MODAL_NAMES.DELIVERY_FEE,
        title: "Delivery Fee Paid by Customers",
        description:
          "Here are the details of the Delivery Fee Paid by Customers:",
        columns: TABLE_COLUMNS.DELIVERY_FEE,
        totalLabel: "Total Delivery Fee",
      },
      {
        name: MODAL_NAMES.TOTAL_PRICE_PAID,
        title: "Total Price Paid by Customers",
        description: "Here are the details of the total price paid by customers:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Amount",
        size: "xl" as const,
      },
      {
        name: MODAL_NAMES.PLATFORM_COMMISSION,
        title: "Platform Commission",
        description: "Here are the details of the Platform Commission:",
        columns: TABLE_COLUMNS.COMMISSION,
        showVAT: true,
        showCommission: true,
        totalLabel: "Platform Commission",
        size: "xl" as const,
      },
      {
        name: MODAL_NAMES.TRANSACTION,
        title: "Transaction Fees",
        description: "Here are the details of the Transaction Fees:",
        columns: TABLE_COLUMNS.TRANSACTION_FEE,
        totalLabel: "Total Transaction Fees",
        size: "xl" as const,
      },
      {
        name: MODAL_NAMES.OTHER_FEE_ONE,
        title: "Other Charges 1 (Handling Charges)",
        description: "Here are the details of the Handling Charges:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Handling Charges",
      },
      {
        name: MODAL_NAMES.OTHER_FEE_TWO,
        title: "Other Charges 2",
        description: "Here are the details of the Other Fees:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Other Fees",
      },
      {
        name: MODAL_NAMES.PLATFORM_DELIVERY_FEE,
        title: "Platform Delivery Fee",
        description: "Here are the details of the Platform Delivery Fee:",
        columns: TABLE_COLUMNS.DELIVERY_FEE,
        totalLabel: "Total Platform Delivery Fee",
      },
      {
        name: MODAL_NAMES.PRODUCT_PRICE_RETURN_WINDOW,
        title: "Product Price - Under Return Window",
        description: "Here are the details of products under return window:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Product Price",
      },
      {
        name: MODAL_NAMES.DELIVERY_FEE_RETURN_WINDOW,
        title: "Delivery Fee - Under Return Window",
        description: "Here are the details of delivery fees under return window:",
        columns: TABLE_COLUMNS.DELIVERY_FEE,
        totalLabel: "Total Delivery Fee",
      },
      {
        name: MODAL_NAMES.PRODUCT_PRICE_RETURN_PROCESSING,
        title: "Product Price - Return/Refund Under Processing",
        description: "Here are the details of products under processing:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Product Price",
      },
      {
        name: MODAL_NAMES.DELIVERY_FEE_RETURN_PROCESSING,
        title: "Delivery Fee - Return/Refund Under Processing",
        description: "Here are the details of delivery fees under processing:",
        columns: TABLE_COLUMNS.DELIVERY_FEE,
        totalLabel: "Total Delivery Fee",
      },
      {
        name: MODAL_NAMES.REFUNDED_TO_CUSTOMER,
        title: "Refunded to Customer",
        description: "Here are the details of refunds to customers:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Refunded",
      },
      {
        name: MODAL_NAMES.COMMISSION_REFUNDED,
        title: "Commission Refunded on Return",
        description: "Here are the details of commission refunded:",
        columns: TABLE_COLUMNS.COMMISSION,
        totalLabel: "Total Commission Refunded",
      },
      {
        name: MODAL_NAMES.TRANSACTION_FEE_REFUNDED,
        title: "Transaction Fee Refunded on Return",
        description: "Here are the details of transaction fees refunded:",
        columns: TABLE_COLUMNS.TRANSACTION_FEE,
        totalLabel: "Total Transaction Fee Refunded",
      },
      {
        name: MODAL_NAMES.OTHER_FEE_ONE_REFUNDED,
        title: "Other Fee 1 Refunded on Return",
        description: "Here are the details of other fee 1 refunded:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Other Fee 1 Refunded",
      },
      {
        name: MODAL_NAMES.OTHER_FEE_TWO_REFUNDED,
        title: "Other Fee 2 Refunded on Return",
        description: "Here are the details of other fee 2 refunded:",
        columns: TABLE_COLUMNS.BASIC_ORDER,
        totalLabel: "Total Other Fee 2 Refunded",
      },
      // Add more modal configurations as needed
    ],
    []
  );

  // Memoized modal close handlers
  const modalCloseHandlers = useMemo(() => {
    const handlers: Record<string, () => void> = {};
    modalConfigurations.forEach((config) => {
      handlers[config.name] = () => closeModal(config.name);
    });
    return handlers;
  }, [modalConfigurations, closeModal]);

  // Memoized modal rendering
  const renderModals = useMemo(() => {
    return modalConfigurations.map(
      (config) =>
        isModalOpen(config.name) && (
          <TransactionModal
            key={config.name}
            show={true}
            onHide={modalCloseHandlers[config.name]}
            title={config.title}
            description={config.description}
            transactionData={transactionData}
            columns={config.columns}
            showVAT={config.showVAT}
            showCommission={config.showCommission}
            totalLabel={config.totalLabel}
            size={config.size}
          />
        )
    );
  }, [modalConfigurations, isModalOpen, modalCloseHandlers, transactionData]);

  const renderAccountTable = useMemo(() => {
    if (loading) {
      return (
        <Row>
          <Col className="d-flex align-items-center justify-content-center">
            <Spinner animation="grow" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Col>
        </Row>
      );
    }

    if (!accountStatementData) {
      return <NoDataFound title="No Earnings" />;
    }

    const tableData = createTableRowData(accountStatementData, modalHandlers);

    return (
      <div className="table-responsive">
        <Table bordered className="account-main-table">
          <thead>
            <tr>
              <th>Items Name</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <TableRenderer
              tableData={tableData}
              expandedRows={expandedRows}
              toggleRow={toggleRow}
            />
          </tbody>
        </Table>
      </div>
    );
  }, [
    loading,
    accountStatementData,
    expandedRows,
    toggleRow,
    modalHandlers,
  ]);

  return (
    <div className="about-us common-page">
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={title}
        info=""
        imageUrl={imageUrl}
      />

      <Section
        className="aboutUs account"
        style={{ paddingTop: 100, paddingBottom: 100 }}
      >
        <Container>
          {/* Summary Cards */}
          <Row className="card-row mb-5 seller-card-row">
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title>Total Sales</Card.Title>
                  <Card.Text>रु{dashboardData?.total_sales ?? "00.00"}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title>Payout Pending</Card.Title>
                  <Card.Text>रु{dashboardData?.payout_pending ?? "00.00"}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title>Payout Released</Card.Title>
                  <Card.Text>रु{dashboardData?.payout_released ?? "00.00"}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title>Refund/Return</Card.Title>
                  <Card.Text>रु{dashboardData?.refund_return ?? "00.00"}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Date Selection Tabs */}
          <Row className="card-row shadow-sm card mx-1 date-card">
            <Col className="px-0">
              <Tabs
                activeKey={activeKey}
                onSelect={handleTabSelect}
                id="fill-tab-example"
                className="mb-3"
                fill
              >
                <Tab eventKey={TAB_KEYS.WEEKLY} title="Earnings by Week">
                  <Row className="justify-content-center p-4 mb-0">
                    <Col md={8}>
                      <div className="store-dates mb-store-dates">
                        <Form.Group className="d-flex align-items-center">
                          <Form.Label className="mb-0" style={{ width: "25%" }}>
                            Select Week
                          </Form.Label>
                          <Form.Select
                            value={selectedWeek}
                            onChange={(e) => handleWeekChange(e.target.value)}
                          >
                            {weeks.map((week, index) => (
                              <option key={index} value={week}>
                                {week}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </Tab>

                <Tab eventKey={TAB_KEYS.CUSTOM_DATES} title="Earnings by Dates">
                  <Row style={styles.card} className="p-4 mb-0">
                    <Col>
                      <div className="store-dates mb-store-dates">
                        From
                        <input
                          type="date"
                          value={tempFromDate}
                          onChange={(e) => setTempFromDate(e.target.value)}
                        />
                        To
                        <input
                          type="date"
                          value={tempToDate}
                          onChange={(e) => setTempToDate(e.target.value)}
                        />
                        <Button onClick={handleGoClick}>Go</Button>
                      </div>
                    </Col>
                  </Row>
                </Tab>
              </Tabs>
            </Col>
          </Row>

          {/* Download Section */}
          <Row className="account-pdf-download">
            <Col sm={5}>
              Download Statement Cycle <br />
              (Date : {fromDate} - {toDate})
            </Col>
            <Col sm={4}>
              {activeKey !== TAB_KEYS.CUSTOM_DATES && (
                <>
                  Statement Cycle Ref.:{" "}
                  {accountStatementData
                    ? accountStatementData.transaction_data
                        .ac_statement_reference
                    : "-"}
                </>
              )}
            </Col>
            <Col sm={2}>Payout Date : N/A</Col>
            <Col sm={1} style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={handleDownload} disabled={pdfLoading}>
                {pdfLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FontAwesomeIcon icon={faDownload as any} />
                )}
              </button>
            </Col>
          </Row>

          {/* Account Statement Table */}
          <Row>
            <Col>{renderAccountTable}</Col>
          </Row>
        </Container>
      </Section>

      {/* Optimized Modals - Only render when open */}
      {renderModals}

      {transactionDataLoader && <FullScreenLoader />}
    </div>
  );
});

Account.displayName = "Account";

export default Account;
